import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { createCase, listMyCases, type CaseRow } from '../lib/supabaseCases';
import { listPendingReviews } from '../lib/supabaseCaseReviews';
import {
  getClarificationCountsForCases,
  type ClarificationCounts,
} from '../lib/supabaseClarifications';
import { getOpenDsgvoRequestSnapshotsForCases, type OpenDsgvoSnapshot } from '../lib/supabaseDsgvo';
import type { CaseReviewRow, CaseStatus } from '../lib/supabaseTypes';
import { LoadingRows } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; cases: CaseRow[] }
  | { kind: 'error'; message: string };

type StatusFilter = 'all' | CaseStatus;

/**
 * Status-Labels + Badge-Styles. Exportiert, damit das Cockpit denselben
 * Badge im Fall-Header zeigen kann (statt einer zweiten Wahrheit).
 */
export const STATUS_LABELS: Readonly<Record<CaseStatus, string>> = {
  draft: 'Entwurf',
  in_progress: 'In Bearbeitung',
  review: 'In Prüfung',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};

export const STATUS_BADGE_STYLES: Readonly<Record<CaseStatus, string>> = {
  draft: 'bg-stone-100 text-stone-700',
  in_progress: 'bg-sky-100 text-sky-800',
  review: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-stone-200 text-stone-500',
};

/**
 * Fuzzy-Match: case-insensitive substring auf client_full_name UND
 * jc_file_number UND case-id-prefix. Leerer Query → alles match.
 *
 * Exportiert (statt nur lokal), damit der Test diese pure Function direkt
 * aufrufen kann ohne den gesamten React-Tree zu mounten — siehe
 * Cases.test.ts. Pure Function mit testbarem Vertrag.
 */
export function matchesSearch(c: CaseRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  const haystack = [c.client_full_name, c.jc_file_number, c.id.slice(0, 8)]
    .filter((v): v is string => typeof v === 'string')
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/**
 * Filter-Funktion: kombiniert Status-Filter + Such-Query in einem Pass.
 * Pure Function — testbar ohne Component.
 */
export function filterCases(
  cases: ReadonlyArray<CaseRow>,
  statusFilter: StatusFilter,
  search: string,
): CaseRow[] {
  return cases
    .filter((c) => statusFilter === 'all' || c.status === statusFilter)
    .filter((c) => matchesSearch(c, search));
}

export type SortKey = 'updated' | 'created' | 'name' | 'status';

/** Sortier-Priorität der Status: was Aufmerksamkeit braucht, zuerst. */
const STATUS_SORT_ORDER: Readonly<Record<CaseStatus, number>> = {
  review: 0,
  in_progress: 1,
  draft: 2,
  completed: 3,
  cancelled: 4,
};

const parseTs = (iso: string | null | undefined): number => {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Sortiert die (bereits gefilterte) Fallliste. Pure Function, nicht-mutierend.
 *  - updated: zuletzt geänderte zuerst (Default, fürs Tagesgeschäft)
 *  - created: neu angelegte zuerst
 *  - name:    Mandantenname A–Z (leere Namen ans Ende)
 *  - status:  nach Handlungsdruck (review → … → cancelled), dann zuletzt geändert
 */
export function sortCases(cases: ReadonlyArray<CaseRow>, sortKey: SortKey): CaseRow[] {
  const arr = [...cases];
  switch (sortKey) {
    case 'updated':
      return arr.sort((a, b) => parseTs(b.updated_at) - parseTs(a.updated_at));
    case 'created':
      return arr.sort((a, b) => parseTs(b.created_at) - parseTs(a.created_at));
    case 'name':
      return arr.sort((a, b) => {
        const an = a.client_full_name ?? '';
        const bn = b.client_full_name ?? '';
        if (an === '' && bn !== '') return 1;
        if (bn === '' && an !== '') return -1;
        return an.localeCompare(bn, 'de');
      });
    case 'status':
      return arr.sort(
        (a, b) =>
          STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status] ||
          parseTs(b.updated_at) - parseTs(a.updated_at),
      );
  }
}

/**
 * Relative Zeitangabe in Sie-tauglichem Deutsch („vor 3 Tagen"). Ab ~4 Wochen
 * fällt sie auf das Datum zurück. `now` ist injizierbar (deterministische Tests).
 * Pure Function.
 */
export function relativeTimeDe(iso: string, now: number = Date.now()): string {
  const t = parseTs(iso);
  if (t === 0) return '';
  const diffSec = Math.max(0, Math.round((now - t) / 1000));
  if (diffSec < 60) return 'gerade eben';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `vor ${min} Minute${min === 1 ? '' : 'n'}`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `vor ${hours} Stunde${hours === 1 ? '' : 'n'}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `vor ${weeks} Woche${weeks === 1 ? '' : 'n'}`;
  return new Date(t).toLocaleDateString('de-DE');
}

/** Status-Counts pro Status-Filter-Tab. Pure Function. */
export function computeStatusCounts(
  cases: ReadonlyArray<CaseRow>,
): Readonly<Record<StatusFilter, number>> {
  const counts: Record<StatusFilter, number> = {
    all: cases.length,
    draft: 0,
    in_progress: 0,
    review: 0,
    completed: 0,
    cancelled: 0,
  };
  for (const c of cases) counts[c.status]++;
  return counts;
}

/**
 * Cases-Übersicht.
 *
 * AP6-Stub: Demo-Akte als Mock.
 * AP11: zusätzlich Live-Daten aus Supabase, sobald Auth nicht im Mock-Modus
 * läuft. Im Dev-Mock zeigen wir weiter den Demo-Pfad — kein Server-Roundtrip.
 */
export function Cases() {
  const navigate = useNavigate();
  const { isMock, user, isLoading } = useAuthOrSession();
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [pendingReviews, setPendingReviews] = useState<CaseReviewRow[]>([]);
  const [clarificationCounts, setClarificationCounts] = useState<
    ReadonlyMap<string, ClarificationCounts>
  >(new Map());
  const [dsgvoSnapshots, setDsgvoSnapshots] = useState<ReadonlyMap<string, OpenDsgvoSnapshot>>(
    new Map(),
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('updated');

  // Gefilterte + sortierte Liste — useMemo, damit das nicht bei jedem
  // Re-Render (z.B. ReviewWorkflow-State-Update im Cockpit-Sync) neu läuft.
  const filteredCases = useMemo(() => {
    if (load.kind !== 'ready') return [];
    return sortCases(filterCases(load.cases, statusFilter, search), sortBy);
  }, [load, search, statusFilter, sortBy]);

  // Status-Counts für die Filter-Tabs (zeigt Berater auf einen Blick,
  // wo etwas zu tun ist).
  const statusCounts = useMemo<Readonly<Record<StatusFilter, number>>>(() => {
    if (load.kind !== 'ready') {
      return { all: 0, draft: 0, in_progress: 0, review: 0, completed: 0, cancelled: 0 };
    }
    return computeStatusCounts(load.cases);
  }, [load]);

  async function refresh() {
    setLoad({ kind: 'loading' });
    try {
      const cases = await listMyCases();
      setLoad({ kind: 'ready', cases });
      // Pending Reviews parallel laden — Fehler hier blocken die Liste nicht.
      try {
        const reviews = await listPendingReviews();
        setPendingReviews(reviews);
      } catch (revErr) {
        console.warn('listPendingReviews failed:', revErr);
        setPendingReviews([]);
      }
      // Phase B: Rückfragen-Counts pro Case laden — defensive: wenn der
      // Backend-View `case_clarification_counts` noch nicht da ist (Backend-
      // Agent läuft parallel), liefert die Library leere Map → kein Badge.
      try {
        const ids = cases.map((c) => c.id);
        const counts = await getClarificationCountsForCases(ids);
        setClarificationCounts(counts);
      } catch (countErr) {
        console.warn('getClarificationCountsForCases failed (non-blocking):', countErr);
        setClarificationCounts(new Map());
      }
      // Phase C5: Offene DSGVO-Anfragen-Snapshots pro Case laden. Defensive
      // wie oben — bei Backend-Fehler einfach keine Badges.
      try {
        const ids = cases.map((c) => c.id);
        const snaps = await getOpenDsgvoRequestSnapshotsForCases(ids);
        setDsgvoSnapshots(snaps);
      } catch (dsgvoErr) {
        console.warn('getOpenDsgvoRequestSnapshotsForCases failed (non-blocking):', dsgvoErr);
        setDsgvoSnapshots(new Map());
      }
    } catch (err) {
      console.error('listMyCases failed:', err);
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Unbekannter Fehler beim Laden.',
      });
    }
  }

  useEffect(() => {
    // Production: erst laden, wenn die Session geprüft UND ein User da ist.
    // Sonst stirbt der Request (ohne Auth-Header) und wir bleiben auf
    // „Lade Mandatsliste…" hängen.
    if (isMock) return;
    if (isLoading) return;
    if (!user) return;
    void refresh();
  }, [isMock, isLoading, user]);

  async function handleCreateDemoCase() {
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createCase({
        type: 'b2g',
        legalBasis: 'par10',
        jobcenterName: 'Demo-Jobcenter (Stadt)',
        jobcenterFileNumber: `IFK-${new Date().getFullYear()}-${Math.floor(Math.random() * 10_000)
          .toString()
          .padStart(4, '0')}`,
      });
      void navigate(`/case/${created.id}/hub`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Anlegen.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="pb-24 pt-12 md:pt-20">
      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-activity)', opacity: 0.32 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Übersicht
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          {de.cases.title}
        </h1>
        <p className="mt-3 font-serif text-base italic text-stone-500 md:text-lg">
          {de.cases.subtitle}
        </p>
      </header>

      {/* Dev-Mock-Pfad: Demo-Akte */}
      {isMock && (
        <section className="rounded-sm border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-6 py-4 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-700">
              Dev-Modus · Mock-Auth
            </p>
            <p className="mt-1 font-serif text-sm italic text-stone-500">
              Ohne Supabase-Backend wird hier nur eine Demo-Akte angeboten. Im Production-Build
              werden echte Cases geladen.
            </p>
          </div>
          <div className="px-6 py-12 text-center md:px-10 md:py-16">
            <p className="font-serif text-base italic text-stone-500">{de.cases.empty}</p>
            <Button
              type="button"
              onClick={() => {
                void navigate('/case/demo/hub');
              }}
              className="mt-8"
            >
              {de.cases.openDemo} →
            </Button>
          </div>
        </section>
      )}

      {/* Production-Pfad: Live-Daten aus Supabase */}
      {!isMock && (
        <>
          {/* Pending-Reviews-Sektion — sichtbar wenn der eingeloggte
              Berater offene Reviews als Reviewer bearbeiten kann.
              RLS filtert serverseitig. */}
          {pendingReviews.length > 0 && (
            <section className="mb-10 rounded-sm border border-amber-200 bg-amber-50">
              <div className="border-b border-amber-200 px-6 py-4 md:px-10">
                <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
                  Vier-Augen-Prinzip · Offene Reviews
                </p>
                <p className="mt-1 font-serif text-sm italic text-amber-900">
                  Diese Mandate warten auf eine zweite Bewertung.
                </p>
              </div>
              <ul className="divide-y divide-amber-200">
                {pendingReviews.map((rev) => (
                  <li key={rev.id}>
                    <button
                      type="button"
                      onClick={() => {
                        void navigate(`/case/${rev.case_id}/review`);
                      }}
                      className="flex w-full items-center justify-between gap-6 px-6 py-4 text-left transition-colors hover:bg-amber-100 md:px-10"
                    >
                      <div className="flex items-start gap-3">
                        <ShieldCheck size={16} className="mt-0.5 flex-shrink-0 text-amber-700" />
                        <div>
                          <p className="font-serif text-base font-bold text-stone-900">
                            {rev.case_id.slice(0, 8).toUpperCase()}
                          </p>
                          <p className="mt-0.5 font-serif text-xs italic text-amber-900">
                            Angefragt am {new Date(rev.requested_at).toLocaleString('de-DE')}
                          </p>
                        </div>
                      </div>
                      <span className="font-sans text-xs font-medium uppercase tracking-widest text-amber-900 hover:underline">
                        Prüfen →
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Auth-State noch ungeklärt → ruhiges Skeleton statt „lädt…"-Text */}
          {isLoading && <LoadingRows rows={2} label="Sitzung wird geprüft" />}

          {/* Auth geklärt, aber kein User → Re-Login anbieten */}
          {!isLoading && !user && (
            <section className="rounded-sm border border-amber-200 bg-amber-50 px-6 py-5 md:px-10">
              <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
                Nicht eingeloggt
              </p>
              <p className="mt-2 font-serif text-sm text-amber-900">
                Ihre Sitzung ist abgelaufen oder konnte nicht hergestellt werden. Bitte erneut
                anmelden.
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void navigate('/login');
                }}
                className="mt-3"
              >
                Zum Login →
              </Button>
            </section>
          )}

          {load.kind === 'loading' && user && (
            <LoadingRows rows={3} label="Mandatsliste wird geladen" />
          )}

          {load.kind === 'error' && (
            <section className="rounded-sm border border-red-200 bg-red-50 px-6 py-5 md:px-10">
              <p className="font-sans text-xs font-medium uppercase tracking-widest text-red-800">
                Fehler beim Laden
              </p>
              <p className="mt-2 font-serif text-sm text-red-800">{load.message}</p>
              <button
                type="button"
                onClick={() => {
                  void refresh();
                }}
                className="mt-3 inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-widest text-red-800 underline"
              >
                <RefreshCw size={12} /> Erneut versuchen
              </button>
            </section>
          )}

          {load.kind === 'ready' && load.cases.length === 0 && (
            <section className="rounded-sm border border-stone-200 bg-white px-6 py-12 text-center md:px-10 md:py-16">
              <p className="font-serif text-base italic text-stone-500">
                Noch kein Mandat angelegt.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button
                  type="button"
                  onClick={() => {
                    void navigate('/case/new');
                  }}
                >
                  <Plus size={14} />
                  Mandat anlegen →
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    void handleCreateDemoCase();
                  }}
                  disabled={creating}
                  className="inline-flex items-center gap-2 rounded-sm border border-stone-300 bg-white px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-600 hover:border-stone-900 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {creating ? 'Lege an…' : 'Demo-Mandat (Quick-Test)'}
                </button>
              </div>
              {createError && <p className="mt-3 font-sans text-xs text-red-700">{createError}</p>}
            </section>
          )}

          {load.kind === 'ready' && load.cases.length > 0 && (
            <section className="space-y-0">
              {/* Header: Anzahl + „Neues Mandat" */}
              <div className="mb-4 flex items-center justify-between">
                <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
                  {load.cases.length} Mandat{load.cases.length === 1 ? '' : 'e'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void navigate('/case/new');
                  }}
                  className="inline-flex items-center gap-2 rounded-sm border border-stone-900 bg-white px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-900 hover:bg-stone-900 hover:text-white"
                >
                  <Plus size={12} />
                  Neues Mandat
                </button>
              </div>

              {/* Such-Box + Sortierung */}
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                {/* Search-Box: client_name, jc_file_number, case-id-Prefix */}
                <div className="relative flex-1">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-500"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    placeholder="Suche nach Mandantenname, Aktenzeichen oder Case-ID…"
                    className="w-full rounded-sm border border-stone-200 bg-white py-2.5 pl-9 pr-9 font-serif text-sm text-stone-900 placeholder:italic placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-0"
                    aria-label="Mandate durchsuchen"
                  />
                  {search.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                      }}
                      aria-label="Suche leeren"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-stone-500 hover:bg-stone-100 hover:text-stone-700"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Sortierung */}
                <div className="flex items-center gap-2 sm:flex-shrink-0">
                  <label
                    htmlFor="cases-sort"
                    className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
                  >
                    {de.cases.sort.label}
                  </label>
                  <select
                    id="cases-sort"
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value as SortKey);
                    }}
                    className="rounded-sm border border-stone-200 bg-white py-2.5 pl-3 pr-8 font-sans text-xs font-medium text-stone-700 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                  >
                    <option value="updated">{de.cases.sort.updated}</option>
                    <option value="created">{de.cases.sort.created}</option>
                    <option value="name">{de.cases.sort.name}</option>
                    <option value="status">{de.cases.sort.status}</option>
                  </select>
                </div>
              </div>

              {/* Status-Filter-Tabs */}
              <div
                className="mb-4 flex flex-wrap gap-1 border-b border-stone-200"
                role="tablist"
                aria-label="Mandate nach Status filtern"
              >
                {(['all', 'in_progress', 'review', 'completed', 'draft'] as const).map((key) => {
                  const isActive = statusFilter === key;
                  const count = statusCounts[key];
                  const label = key === 'all' ? 'Alle' : STATUS_LABELS[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => {
                        setStatusFilter(key);
                      }}
                      className={`relative -mb-px min-h-[44px] border-b-2 px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 ${
                        isActive
                          ? 'border-stone-900 text-stone-900'
                          : 'border-transparent text-stone-600 hover:text-stone-900'
                      }`}
                    >
                      {label}
                      <span
                        className={`ml-2 inline-block min-w-[1.5em] rounded-sm px-1.5 py-0.5 text-center text-[10px] tabular-nums ${
                          isActive ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Empty-Set bei aktivem Filter */}
              {filteredCases.length === 0 && (
                <p className="border-y border-stone-200 py-12 text-center font-serif text-sm italic text-stone-500">
                  Keine Mandate für diesen Filter.{' '}
                  {(search.length > 0 || statusFilter !== 'all') && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setStatusFilter('all');
                      }}
                      className="ml-1 font-sans text-xs uppercase tracking-widest text-stone-700 underline"
                    >
                      Filter zurücksetzen
                    </button>
                  )}
                </p>
              )}

              {/* Mandate-Liste */}
              {filteredCases.length > 0 && (
                <ul className="divide-y divide-stone-200 border-y border-stone-200">
                  {filteredCases.map((c) => {
                    const counts = clarificationCounts.get(c.id);
                    const dsgvo = dsgvoSnapshots.get(c.id);
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            void navigate(`/case/${c.id}/hub`);
                          }}
                          className="group flex w-full items-start justify-between gap-6 py-6 text-left transition-colors hover:bg-stone-50 focus-visible:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
                                {c.type === 'b2g' ? 'B2G · Jobcenter' : 'B2C · Mandant'} ·{' '}
                                {legalBasisLabel(c.legal_basis)}
                              </p>
                              <span
                                className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${STATUS_BADGE_STYLES[c.status]}`}
                              >
                                {STATUS_LABELS[c.status]}
                              </span>
                            </div>
                            <p className="mt-1 truncate font-serif text-lg font-bold text-stone-900">
                              {c.client_full_name ?? '— Mandantenname noch nicht erfasst —'}
                            </p>
                            <p className="mt-1 font-serif text-xs italic text-stone-600">
                              {c.jc_file_number
                                ? `Aktenzeichen ${c.jc_file_number} · `
                                : `Case ${c.id.slice(0, 8).toUpperCase()} · `}
                              angelegt {new Date(c.created_at).toLocaleDateString('de-DE')} ·{' '}
                              {de.cases.changedPrefix} {relativeTimeDe(c.updated_at)}
                            </p>
                            {/* Phase B: Rückfragen-Badges */}
                            {counts && (counts.unanswered > 0 || counts.awaitingResolution > 0) && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                {counts.unanswered > 0 && (
                                  <span
                                    className="inline-flex items-center gap-1 rounded-sm bg-amber-100 px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest text-amber-900"
                                    title="Mandant hat noch nicht geantwortet"
                                  >
                                    <MessageSquare size={10} aria-hidden="true" />
                                    Mandant antwortet noch · {counts.unanswered}
                                  </span>
                                )}
                                {counts.awaitingResolution > 0 && (
                                  <span
                                    className="inline-flex items-center gap-1 rounded-sm bg-sky-100 px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest text-sky-900"
                                    title="Beantwortet — wartet auf Berater-Freigabe"
                                  >
                                    <MessageSquare size={10} aria-hidden="true" />
                                    Antwort warten auf Freigabe · {counts.awaitingResolution}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Phase C5: DSGVO-Anfragen-Badge */}
                            {dsgvo && dsgvo.count > 0 && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <span
                                  className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${
                                    dsgvo.isExpired
                                      ? 'bg-red-100 text-red-900'
                                      : daysUntilDeadlineSafe(dsgvo.nearestDeadline) < 7
                                        ? 'bg-amber-100 text-amber-900'
                                        : 'bg-stone-100 text-stone-700'
                                  }`}
                                  title="Offene DSGVO-Anfrage — siehe Cockpit"
                                >
                                  <AlertTriangle size={10} aria-hidden="true" />
                                  {dsgvo.isExpired
                                    ? de.dsgvo.cockpit.casesListBadgeExpired
                                    : de.dsgvo.cockpit.casesListBadge.replace(
                                        '{days}',
                                        String(
                                          Math.max(0, daysUntilDeadlineSafe(dsgvo.nearestDeadline)),
                                        ),
                                      )}
                                </span>
                              </div>
                            )}
                          </div>
                          <span className="flex-shrink-0 self-center font-sans text-xs font-medium uppercase tracking-widest text-stone-900 group-hover:underline">
                            Öffnen →
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {createError && <p className="mt-3 font-sans text-xs text-red-700">{createError}</p>}
            </section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Helper: berechnet Tage bis Frist, gibt 0 zurück bei kaputtem ISO. Wir
 * importieren bewusst nicht die analoge Funktion aus supabaseDsgvo, weil
 * dies eine reine UI-Hilfe ist (Defensive für leere/kaputte Strings) — die
 * Kern-Library-Funktion `daysUntilDeadline` wird in der Berater-Sektion und
 * in der Mandant-Self-Service-Page genutzt.
 */
function daysUntilDeadlineSafe(iso: string): number {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 0;
  return Math.floor((ms - Date.now()) / (24 * 60 * 60 * 1000));
}

function legalBasisLabel(b: string): string {
  switch (b) {
    case 'par10':
      return '§ 10 Abs. 2 Nr. 5 SGB II';
    case 'par16b':
      return '§ 16b SGB II Einstiegsgeld';
    case 'par16c':
      return '§ 16c SGB II Förderung Selbständigkeit';
    case 'par93':
      return '§ 93 SGB III Gründungszuschuss';
    default:
      return b;
  }
}
